import { MediaType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { token } from '@lastbrand/web-ui-styled-system/tokens'

import { FeatureCardsDS } from '@src/components/blocks/feature-cards-ds'
import type { FeatureCardsButton, FeatureCardsRow } from '@src/components/blocks/feature-cards-ds'
import { FeatureInfoDS } from '@src/components/blocks/feature-info-ds'
import type { FeatureInfoSection } from '@src/components/blocks/feature-info-ds'
import type { PLTypedWidgetProps } from '@src/components/pl-base-widget'
import {
  getFeatureSectionTwoAnalytics,
  useFeatureSectionTwoImpressionRef,
} from '@src/components/widgets/use-ia-impression'

import { resolveSectionBackground } from '../widget-background'
import { getSafeHref, isFS2NewDesignShape, toRichText } from '../widget-utils'

import type { FeatureCardItem, FeatureCardsWidgetData, Media } from '@lastbrand/storefront-proto-registry/api/v1/widget'

// Matches legacy's root testid verbatim so QA can select the widget root the same way in either mode.
// @i18n-ignore
const FEATURE_CARDS_WIDGET_TEST_ID = 'feature-cards-widget'

// Default band for FS2-shaped sections when the CMS authored no `background-color--*` class.
//
// STRFRNT-16715: this fork had no fallback, so the band resolved to `undefined` and the section
// rendered transparent on the DS PL path — while BOTH of its twins painted one. The legacy PL fork
// falls back to `$off-white` (`feature-cards-widget-legacy.tsx`), and the non-PL DS twin
// (`blocks/feature-info-ds`) hardcodes `paperwhite`. That made the miss invisible unless you ran
// `is_ds=true` AND `plEnabled=true` together, which is the one combination nothing else covers.
//
// Note this is not a "pending BE contract" gap that will fix itself: `classNames` is absent from the
// proto today, so `resolveSectionBackground` returns `undefined` for EVERY authored section on this
// path — the fallback is what paints the band in practice, not an edge case.
//
// `paperwhite`, not legacy's `$off-white`: the target for a DS-path section is its DS twin, so the
// band matches whichever fork renders. (`paperwhite` #F7F7F5 is the DS token; legacy's off-white
// #eeeeec is a shade darker. Same vocabulary would call this `background-color--offWhiteHover`.)
const FS2_DS_BAND = token('colors.paperwhite')
// @i18n-ignore
const FEATURE_CARD_KEY_FALLBACK = 'feature-card'

// Extracts `{ label, href }` from a raw proto button, tolerating proto/Contentful field names.
function normalizeButton(raw: Record<string, unknown>): FeatureCardsButton | null {
  const label = typeof raw.label === 'string' ? raw.label : typeof raw.title === 'string' ? raw.title : undefined
  if (!label) return null
  const link = raw.link as Record<string, unknown> | undefined
  const url = (link?.url as string | undefined) ?? (link?.navigationPath as string | undefined)
  return { label, href: getSafeHref(url) }
}

const isVideoMedia = (media?: Media): boolean => media?.type === MediaType.VIDEO

// Maps the PL proto shape onto `FeatureInfoDS`'s structural props, the Figma `Widget-FeatureInfo` band
// (beauty lg `9562:169871`, sm `9562:169756`) that the non-PL FS2 section already renders for the same
// content. Per-item `media` is the card visual (a video when the proto says so), `icon` the glyph
// beneath it, `subTitle` the description, and the section `heading` the band title. Urls are passed
// raw — the block normalizes protocol-relative Contentful urls itself.
function toFeatureInfoProps(
  data: { heading?: string } | undefined,
  items: readonly FeatureCardItem[],
): FeatureInfoSection {
  return {
    displayTitle: data?.heading,
    subSections: items.map((item, index) => {
      const content = item.media?.content
      const document = toRichText(item.subTitle)?.raw
      const iconContent = item.icon?.content
      return {
        // Title alone isn't unique (two cards can share copy) → suffix the index for a stable key.
        id: `${item.title ?? FEATURE_CARD_KEY_FALLBACK}-${index}`,
        displayTitle: item.title,
        visual: { url: content?.url, title: content?.alt },
        images: iconContent ? [{ url: iconContent.url, title: iconContent.alt }] : undefined,
        videos:
          isVideoMedia(item.media) && content?.url
            ? [{ url: content.url, contentType: content.contentType, title: content.alt }]
            : undefined,
        richTexts: document?.content.length ? [{ raw: document }] : undefined,
      }
    }),
  }
}

/**
 * DS branch of the PL `FEATURE_CARDS` widget.
 *
 * FS2 "new design"-shaped data (`isFS2NewDesignShape` — 2–4 cards, each with its own media and icon)
 * renders as `FeatureInfoDS`, matching what the non-PL section does with the same content
 * (`image-with-text-product-details-section-ds.tsx` routes `isFS2NewDesignDataValid` there). The
 * previous `isSingleHeroFs2Shape` gate additionally demanded that every card share ONE media url,
 * which no real 4-photo FS2 section can satisfy, so this path was unreachable and every section fell
 * through to the plain card grid (STRFRNT-16748).
 *
 * Everything else normalizes each proto item into a typed `FeatureCardsRow` (rich text → Contentful
 * `Document`, buttons → safe `{ label, href }`) and delegates to `FeatureCardsDS`. FS2-shaped data
 * carrying buttons stays on that path deliberately: `FeatureInfoDS` renders no CTAs, and dropping
 * authored content is what PR #7487's review blocked. `classNames` are cast until the proto carries them.
 */
export function FeatureCardsDSWidget(props: PLTypedWidgetProps<FeatureCardsWidgetData>) {
  const data = props.data.featureCardsData
  const items = data?.items ?? []
  const classNames = (data as { classNames?: string[] } | undefined)?.classNames
  // FS2 `feature_section_two_viewed` impression on scroll-into-view (IA-only). Hook is called
  // unconditionally (Rules of Hooks); the ref is only attached below for FS2-shaped sections.
  const impressionRef = useFeatureSectionTwoImpressionRef(getFeatureSectionTwoAnalytics(data))

  const isFeatureSectionTwo = isFS2NewDesignShape(items)
  const hasButtons = items.some(item => item.buttons.length > 0)

  // `FeatureInfoDS` is a self-contained full-bleed band (its own paperwhite background, no
  // `className`/`backgroundColor` slot), so it supersedes the ad hoc tint this branch layers on top of
  // `FeatureCardsDS` — the impression ref still wraps the rendered output.
  if (isFeatureSectionTwo && !hasButtons) {
    return (
      <div ref={impressionRef}>
        <FeatureInfoDS sectionDetails={toFeatureInfoProps(data, items)} testId={FEATURE_CARDS_WIDGET_TEST_ID} />
      </div>
    )
  }

  const rows: FeatureCardsRow[] = items.map((item, index) => {
    const document = toRichText(item.subTitle)?.raw
    const buttons = (item.buttons as readonly Record<string, unknown>[])
      .map(normalizeButton)
      .filter((button): button is FeatureCardsButton => button !== null)
    return {
      // Title alone isn't unique (two cards can share copy) → suffix the index for a stable key.
      key: `${item.title ?? FEATURE_CARD_KEY_FALLBACK}-${index}`,
      media: item.media,
      icon: item.icon,
      title: item.title,
      subTitle: document?.content.length ? document : undefined,
      buttons,
    }
  })

  const featureCards = (
    <FeatureCardsDS
      heading={data?.heading}
      rows={rows}
      // Authored `classNames` win; otherwise FS2-shaped sections get the DS band (see `FS2_DS_BAND`).
      backgroundColor={resolveSectionBackground(classNames) ?? (isFeatureSectionTwo ? FS2_DS_BAND : undefined)}
      testId={FEATURE_CARDS_WIDGET_TEST_ID}
    />
  )

  // FS2-shaped data that fell back to the card grid (it carries buttons) is still a feature-section-two
  // for analytics — keep the impression ref on it.
  return isFeatureSectionTwo ? <div ref={impressionRef}>{featureCards}</div> : featureCards
}
